import { Intent } from "@blueprintjs/core";
import { Button } from "components/buttons";
import { DateToLocal } from "components/dates";
import { Filter, FilterButton } from "components/filtering";
import { FormTextInput } from "components/form-fields";
import { ShowHide } from "components/layout";
import { ListingTable } from "components/listing";
import { NotificationToaster } from "components/notifications";
import { UserAccess } from "components/user-access";
import { useDebounce } from "hooks/useDebounce";
import { DocumentsUploadModal } from "pages/documents/shared/upload-modal";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { DocumentsService } from "services/DocumentsService";
import { SetFiltering, SetRouting } from "state/actions";


export function DocumentEntityListing({
    entityType,
    entityId,
    documentTypeId,
    showTitle = true,
    filterName = 'documents',
    loading = false,
    ...rest
}) {
    const defaultRecordSize = 25;
    const debounceTimeout = 750;

    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState(true);
    const [initalSetupComplete, setInitalSetupComplete] = useState(false);

    const loadingValue = isLoading || loading;
    const useFilters = !documentTypeId;

    const [uploadModalVisible, setUploadModalVisible] = useState(false);

    const tableHeaders = useMemo(() => {
        const newHeaders = [];

        if (!documentTypeId) {
            newHeaders.push("Type");
        }

        newHeaders.push(
            "Document Name",
            "Version",
            "Area",
            "Uploaded By",
            "Uploaded On");

        return newHeaders;
    }, [documentTypeId]);

    const sortableHeaders = [
        { headerName: "Document Name", sortName: "FileName" },
        { headerName: "Type", sortName: "Type.Name" },
        { headerName: "Version", sortName: "Version" },
        { headerName: "Area", sortName: "Type.Area" },
        { headerName: "Uploaded By", sortName: "UploadedBy" },
        { headerName: "Uploaded On", sortName: "UploadedOn" }
    ];

    const [options, setOptions] = useState({
        documentAreas: []
    });

    const [sortedBy, setSortedBy] = useState(sortableHeaders[sortableHeaders.length - 1]);
    const [sortedDir, setSortedDir] = useState("D");

    const [totalRecords, setTotalRecords] = useState(0);
    const [tableRows, setTableRows] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);

    const [showFilters, setShowFilters] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [pageFilters, setPageFilters] = useState([]);

    const currentPageNumber = useRef();
    const currentTableSortBy = useRef();
    const currentTableSortDir = useRef();
    const currentSearchTerm = useRef();
    const currentAppliedFilters = useRef();

    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);

    const selectedFilters = useSelector(state => state.filters[filterName]);

    const isOrganisationEntityType = entityType === "Organisation";
    const entityIsValid = entityType != null && (entityId || isOrganisationEntityType);

    if (!selectedFilters) {
        dispatch(SetFiltering(filterName, []))
    }

    function onPagingChange(newPageNumber) {
        setPageNumber(newPageNumber);
    }

    function onFilterChange(listingFilters) {
        setAppliedFilters(listingFilters);
    }

    function onToggleFilter() {
        setShowFilters((prevState) => {
            return !prevState;
        });
    }

    function onTableSort(header, direction) {
        setSortedDir(direction);
        setSortedBy(header);
    }

    function onSearchChange(item) {
        setSearchTerm(item.target.value);
    }

    function getAreaName(area) {
        return options.documentAreas.find(a => a.key === area)?.name;
    }

    function mapDocument(document) {
        const rows = [];

        if (documentTypeId) {
            rows.push(
                <Link key={document.id} to={`/document-settings/documents/${document.id}`}>{document.fileName}</Link>
            );
        } else {
            rows.push(
                <Link key={document.id} to={`/document-settings/documents/${document.id}`}>{document.type.name}</Link>,
                document.fileName
            );
        }

        rows.push(
            document.version,
            getAreaName(document.type.area),
            document.uploadedBy,
            <DateToLocal
                key={document.id}
                format="DD/MM/YYYY HH:mm"
            >{document.uploadedOn}</DateToLocal>);

        return rows;
    }

    function fetchTableData(forceRefresh) {
        if (initalSetupComplete && entityIsValid && !rest.loading) {
            if (
                currentPageNumber.current === pageNumber &&
                currentTableSortBy.current === sortedBy &&
                currentTableSortDir.current === sortedDir &&
                currentSearchTerm.current === debouncedSearchTerm &&
                currentAppliedFilters.current === appliedFilters &&
                !forceRefresh
            ) {
                //If none of the values have changed, then 2 have been updated at once, so prevent the listing from performing another call.
                return;
            }

            currentPageNumber.current = pageNumber;
            currentTableSortBy.current = sortedBy;
            currentTableSortDir.current = sortedDir;
            currentSearchTerm.current = debouncedSearchTerm;
            currentAppliedFilters.current = appliedFilters;

            setIsLoading(true);

            const filterRequest = useFilters ? (currentAppliedFilters.current ?? []).map(f => ({
                key: f.name,
                ...f,
            })) : [];

            if (documentTypeId) {
                filterRequest.push({
                    key: 'Type',
                    value: documentTypeId.toString()
                });
            }

            const entityFilter = isOrganisationEntityType ? null : {
                entityId: entityId,
                entityType: entityType
            };
            const searchRequest = {
                requestCount: defaultRecordSize,
                pageNumber: pageNumber,
                sortField: sortedBy.sortName,
                sortDirection: sortedDir,
                filters: filterRequest,
                entityFilter: entityFilter,
                searchTerm: debouncedSearchTerm,
            };

            DocumentsService.getDocuments(searchRequest).then(
                (r) => {
                    let data = [];

                    if (r?.data != null) {
                        data = r.data.map(mapDocument);
                    }

                    setTableRows(data);
                    setTotalRecords(r.totalCount);
                    setIsLoading(false);
                },
                () => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Could not retrieve documents, please try again"
                    );
                }
            );
        }
    }

    useEffect(() => {
        fetchTableData(false);
    }, [
        pageNumber,
        sortedBy,
        sortedDir,
        debouncedSearchTerm,
        initalSetupComplete,
        appliedFilters,
        loading
    ]);

    useEffect(() => {
        DocumentsService.getDocumentOptions()
            .then(o => {
                setOptions(o)
                setPageFilters([
                    {
                        name: 'AREA',
                        displayName: 'Area',
                        items: o.documentAreas.map(a => ({
                            id: a.key,
                            name: a.name
                        }))
                    }
                ])
                setInitalSetupComplete(true);
            });
    }, []);

    useEffect(() => {
        if (entityType === "Organisation") {
            dispatch(SetRouting("Document Type", "document-settings/types" , documentTypeId));
        } else {
            dispatch(SetRouting(entityType, entityType, entityId));
        }
    }, [entityType, entityId, documentTypeId]);

    const onUploadComplete = (anyErrors) => {
        if (anyErrors) {
            NotificationToaster.show(Intent.DANGER, "Some files failed to upload. Please try again.");
        } else {
            NotificationToaster.show(Intent.SUCCESS, "All files uploaded successfully.");
        }

        setUploadModalVisible(false);
        fetchTableData(true);
    }

    return (
        <div>
            {showTitle && <h1>Documents</h1>}
            <UserAccess perform="Documents:Upload"
                yes={() => <div className="button-row spacer-bottom">
                    <Button
                        text={"Upload"}
                        intent="primary"
                        id="upload-document-button"
                        icon="export"
                        disabled={loadingValue}
                        onClick={() => setUploadModalVisible(true)}
                    />
                </div>}
            />
            <div className="filter-controls">
                <div className="inline-items">
                
                    <div className="inline-item fill">
                        <div className="form-field-full">
                            <FormTextInput
                                placeholder="Search documents"
                                onChange={onSearchChange}
                                value={searchTerm}
                                large
                                disabled={isLoading}
                                icon="search"
                                id="document-search-field"
                            />
                        </div>
                    </div>
                    <ShowHide
                        evaluator={useFilters && !rest.loading}
                        show={
                            <div className="inline-item">
                                <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                            </div>
                        }     
                    />               
                </div>
                <Filter
                        filterName={filterName}
                        filters={pageFilters}
                        visible={showFilters}
                        onUpdate={onFilterChange}
                        onClickOutside={onToggleFilter}
                    />
            </div>
            <ListingTable
                showPerPage={false}
                headers={tableHeaders}
                data={tableRows}
                loadingData={loadingValue}
                sortable
                sortedDir={sortedDir}
                sortedBy={sortedBy}
                onSort={onTableSort}
                sortableHeaders={sortableHeaders}
                pageSize={defaultRecordSize}
                onPagingChange={onPagingChange}
                totalRecordCount={totalRecords}
            />
            <DocumentsUploadModal
                onClose={() => setUploadModalVisible(false)}
                isOpen={uploadModalVisible}
                entityType={entityType}
                onUploadComplete={onUploadComplete}
                entityId={entityId}
                prefilledDocumentTypeId={documentTypeId}
            />
        </div>
    );
}

DocumentEntityListing.propTypes = {
    entityId: PropTypes.number,
    entityType: PropTypes.string.isRequired,
    documentTypeId: PropTypes.number,
    loading: PropTypes.bool,
    filterName: PropTypes.string,
    showTitle: PropTypes.bool
};